import { collection, query, where, doc, getDoc, FirestoreError } from 'firebase/firestore';
import { db } from '../config';
import { getDocsOnline, FirestoreOfflineError } from './getDocsOnline';

export interface FirestoreSiteDoc {
  id: string;
  slug?: string;
  status?: string;
  name?: string | Record<string, string>;
  description?: Record<string, string>;
  languages?: Record<string, Record<string, unknown>>;
  branding?: { logo?: string; theme?: string; [key: string]: unknown };
  settings?: {
    identificationMethods?: string[];
    accessControl?: { type?: string; [key: string]: unknown };
    terminology?: string | null;
    nextUrl?: string;
    mapDisplay?: string;
    showProgress?: boolean;
    [key: string]: unknown;
  };
  venueConfig?: { venueType?: string; [key: string]: unknown };
  [key: string]: unknown;
}

const COLLECTION_NAME = 'apps';
const MAX_RETRIES = 3;
const INITIAL_DELAY_MS = 500;

function isNetworkError(err: unknown): boolean {
  if (err instanceof FirestoreOfflineError) return true;
  if (err instanceof FirestoreError) {
    return ['unavailable', 'deadline-exceeded', 'resource-exhausted', 'cancelled'].includes(err.code);
  }
  const msg = (err as Error)?.message?.toLowerCase() ?? '';
  return msg.includes('network') || msg.includes('offline') || msg.includes('transport') || msg.includes('failed to fetch');
}

async function withRetry<T>(fn: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (!isNetworkError(err) || attempt === MAX_RETRIES - 1) throw err;
      await new Promise(r => setTimeout(r, INITIAL_DELAY_MS * 2 ** attempt));
    }
  }
  throw lastError;
}

/**
 * Find a published site by its URL slug.
 * Retries up to 3 times on transient network errors — including an offline
 * client answering from its empty cache (see getDocsOnline).
 */
export async function findSiteBySlug(slug: string): Promise<FirestoreSiteDoc | null> {
  const snapshot = await withRetry(() => {
    const q = query(
      collection(db!, COLLECTION_NAME),
      where('slug', '==', slug),
      where('status', '==', 'published')
    );
    return getDocsOnline(q);
  });
  if (snapshot.empty) return null;
  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Get a site by its document ID.
 */
export async function findSiteById(siteId: string): Promise<FirestoreSiteDoc | null> {
  const docRef = doc(db!, COLLECTION_NAME, siteId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() };
}
